import React, { useEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { FilePlus2, ArrowUpRight, Pencil, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { nominatorApi, useNominatorAuth } from '@/context/NominatorAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status → chip styling. Kept generous so a stray status from the backend
// still renders as a sensible neutral badge.
const STATUS_CHIP = {
  submitted:       { bg: 'bg-[#E0D2BB]',        text: 'text-[#4E3B2A]', label: 'Submitted' },
  in_review:       { bg: 'bg-[#D9B26A]/30',     text: 'text-[#6B4F38]', label: 'In Review' },
  shortlisted:     { bg: 'bg-[#9F6A2C]',        text: 'text-white',      label: 'Shortlisted' },
  winner:          { bg: 'bg-[#4E3B2A]',        text: 'text-[#D9B26A]', label: 'Winner' },
  draft:           { bg: 'bg-[#F5EDE0]',        text: 'text-[#6B4F38] border border-[#B8884A]/50', label: 'Draft' },
  payment_pending: { bg: 'bg-[#C2613A]/15',     text: 'text-[#C2613A]', label: 'Payment Due' },
};
const PAY_CHIP = {
  paid:   { bg: 'bg-[#E0D2BB]',    text: 'text-[#4E3B2A]', label: 'Paid' },
  unpaid: { bg: 'bg-[#C2613A]/15', text: 'text-[#C2613A]', label: 'Unpaid' },
};

const PortalEntries = () => {
  const { nominator } = useNominatorAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDeadline, setEditDeadline] = useState(null);
  const [editDeadlinePassed, setEditDeadlinePassed] = useState(false);
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'all';  // all | draft | paid | pending

  useEffect(() => {
    if (!nominator) { setLoading(false); return; }
    nominatorApi.get('/nominator/nominations')
      .then((r) => setEntries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
    axios.get(`${API}/settings/public`)
      .then((r) => {
        setEditDeadline(r.data?.edit_deadline || null);
        setEditDeadlinePassed(!!r.data?.edit_deadline_passed);
      })
      .catch(() => {});
  }, [nominator]);

  // Client-side filtering keyed off the ?filter= param — matches the stat
  // card intent from the dashboard (Drafts/Paid/Payment Due).
  const filtered = useMemo(() => {
    if (filter === 'draft')   return entries.filter((e) => e.status === 'draft');
    if (filter === 'paid')    return entries.filter((e) => e.payment_status === 'paid');
    if (filter === 'pending') return entries.filter((e) => e.status === 'payment_pending' && e.payment_status !== 'paid');
    return entries;
  }, [entries, filter]);

  const drafts = filtered.filter((e) => e.status === 'draft');
  const rest   = filtered.filter((e) => e.status !== 'draft');

  const deadlineDisplay = editDeadline
    ? new Date(editDeadline + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const setFilter = (val) => {
    const p = new URLSearchParams(params);
    if (val === 'all') p.delete('filter'); else p.set('filter', val);
    setParams(p, { replace: false });
  };

  return (
    <div data-testid="portal-entries">
      <Seo title="Portal Entries · AdAura Awards" description="Private area — not indexed." path="/" noindex />
      {/* Back to dashboard — always available so users have a fast escape
          without needing the browser back button. */}
      <Link to="/portal/dashboard" data-testid="portal-back-dashboard"
            className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.28em] uppercase text-[var(--ink-soft)] hover:text-[var(--walnut)] mb-5 -mt-1">
        <ArrowLeft size={13} /> Back to Dashboard
      </Link>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#9F6A2C]">Track Progress</div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-[-0.01em] leading-tight text-[var(--ink)] mt-2">
            My <span className="italic text-[var(--brand-brass)]">Entries</span>
          </h1>
          {deadlineDisplay && (
            <p className="text-[12px] text-[var(--ink-soft)] mt-2" data-testid="edit-deadline-note">
              {editDeadlinePassed
                ? `Editing is now closed. Submitted entries are locked as of ${deadlineDisplay}.`
                : `You can edit submitted entries until ${deadlineDisplay}.`}
            </p>
          )}
        </div>
        <Link to="/portal/new" className="btn-solid" data-testid="portal-entries-new-cta">
          <FilePlus2 size={14} /> New Nomination
        </Link>
      </div>

      {/* Filter chips — mirror the dashboard's stat cards, so a card click
          lands here with the right filter pre-selected. */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="portal-entries-filters">
        {[
          ['all', 'All'], ['draft', 'Drafts'], ['pending', 'Payment Due'], ['paid', 'Paid'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                  className={`text-[10px] tracking-[0.28em] uppercase px-3 py-2 border transition-colors ${
                    filter === k
                      ? 'bg-[var(--walnut)] text-white border-[var(--walnut)]'
                      : 'bg-white text-[var(--ink)] border-[var(--gold-soft)] hover:border-[var(--walnut)]'
                  }`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[var(--ink-muted)] text-sm">Loading your entries…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#B8884A]/40 p-10 md:p-16 text-center" data-testid="portal-entries-empty">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#9F6A2C]">
            {filter === 'all' ? 'No entries yet' : `No ${filter === 'pending' ? 'payment-due' : filter} entries`}
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-[var(--ink)] mt-3">
            {filter === 'all' ? 'Your slate is blank.' : 'Nothing here yet.'}
          </h3>
          <p className="text-[var(--ink-muted)] text-sm mt-4 max-w-md mx-auto leading-relaxed">
            {filter === 'all'
              ? 'Submit your first AdAura Awards 2026 nomination — it takes about 15 minutes.'
              : 'Try clearing the filter to see all your entries.'}
          </p>
          {filter === 'all' ? (
            <Link to="/portal/new" className="btn-solid mt-7 inline-flex">
              Begin nomination <ArrowUpRight size={14} />
            </Link>
          ) : (
            <button onClick={() => setFilter('all')} className="btn-outline mt-7 inline-flex" data-testid="clear-filter">
              Show all entries
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {drafts.length > 0 && (
            <section data-testid="portal-drafts">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-3">Drafts · resume anytime</div>
              <div className="border border-[#B8884A]/30 bg-white">
                {drafts.map((e, i) => <EntryRow key={e.id || i} entry={e} idx={i} isDraft editDeadlinePassed={editDeadlinePassed} />)}
              </div>
            </section>
          )}
          {rest.length > 0 && (
            <section>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-3">Submitted entries</div>
              <div className="border border-[#B8884A]/30 bg-white">
                {rest.map((e, i) => <EntryRow key={e.id || i} entry={e} idx={i} editDeadlinePassed={editDeadlinePassed} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

const EntryRow = ({ entry, idx, isDraft = false, editDeadlinePassed = false }) => {
  const status = STATUS_CHIP[entry.status] || STATUS_CHIP.submitted;
  const pay = PAY_CHIP[entry.payment_status] || PAY_CHIP.unpaid;
  const title = entry.entry_title || entry.campaign_name || 'Untitled entry';
  const cat = entry.category || '—';
  // Submitted entries can be edited if the deadline has not passed.
  const canEditSubmitted = !isDraft && entry.status === 'submitted' && !editDeadlinePassed;
  return (
    <div className={`flex flex-wrap items-center gap-5 p-5 md:p-6 ${idx > 0 ? 'border-t border-[#B8884A]/20' : ''}`}
         data-testid={`portal-entry-row-${idx}`}>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[var(--ink)] text-base md:text-lg leading-tight truncate">{title}</div>
        <div className="text-[var(--ink-muted)] text-[12.5px] mt-1">{cat}</div>
      </div>
      <span className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 ${status.bg} ${status.text}`}>{status.label}</span>
      {!isDraft && (
        <span className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 ${pay.bg} ${pay.text}`}>{pay.label}</span>
      )}
      {isDraft && (
        <Link to={`/portal/new?draft_id=${entry.id}`}
              className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.28em] uppercase text-[var(--walnut)] hover:text-[var(--gold)]"
              data-testid={`portal-entry-resume-${idx}`}>
          <Pencil size={12} /> Resume
        </Link>
      )}
      {canEditSubmitted && (
        <Link to={`/portal/new?draft_id=${entry.id}`}
              className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.28em] uppercase text-[var(--walnut)] hover:text-[var(--gold)]"
              data-testid={`portal-entry-edit-${idx}`}>
          <Pencil size={12} /> Edit
        </Link>
      )}
    </div>
  );
};

export default PortalEntries;
